import { AppdataService } from './../../../services/app-data/appdata.service';
import { ChangeDetectorRef, Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { CommunicationInterfaceService } from 'src/app/services/communicationInterface/communication-interface.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-select-camera',
  templateUrl: './select-camera.component.html',
  styleUrls: ['./select-camera.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SelectCameraComponent implements OnInit {
  cameraGrp: UntypedFormGroup;
  cameraValues = [];
  title: string;
  message: any;
  selectedValue: any;
  appConfig: any;
  appLabels: any;
  actionsToRight: boolean;
  confirmToRight: boolean;
  snackbarHorizontalPosition: any;
  snackbarVerticalPosition: any;

  constructor(
    public dialogRef: MatDialogRef<SelectCameraModel>,
    @Inject(MAT_DIALOG_DATA) public data: SelectCameraComponent,
    private fb: UntypedFormBuilder,
    private commInterface: CommunicationInterfaceService,
    private appdataService: AppdataService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {
    console.log(data);
    this.title = data.title;
    this.cameraValues = commInterface.availableInputDevices.video;

    this.appdataService?.labelObservable?.subscribe((updatedLabels: any) => {
      this.appLabels = updatedLabels;
      this.cdr.detectChanges();
    })
  }

  ngOnInit(): void {
    try {
      this.cameraGrp = this.fb.group({
        options: [this.cameraValues[0], [Validators.required]]
      });
      this.onChanges();
      if (this.commInterface.chosenCameraDevice) {
        this.selectedValue = this.commInterface.chosenCameraDevice;
        this.cameraGrp.controls.options.setValue(this.selectedValue);
      } else this.selectedValue = this.cameraValues[0];
      this.appConfig = this.appdataService.getConfig();
      this.appLabels = this.appdataService.getLabels();
      this.snackbarHorizontalPosition = this.appConfig?.Miscellaneous?.snackbarPosition?.horizontalPosition || 'center';
      this.snackbarVerticalPosition = this.appConfig?.Miscellaneous?.snackbarPosition?.verticalPosition || 'top';
      this.actionsToRight = this.appConfig.Miscellaneous?.ButtonArrangement?.actionsToRight;
      this.confirmToRight = this.appConfig.Miscellaneous?.ButtonArrangement?.confirmToRight;
    } catch (error) {
      console.error('%c Error Occured in ngOnInit', 'color: red', error);
      this.commInterface.sendUIErrors('SelectcameraComponent', 'ngOnInit', JSON.stringify(error));
      this.commInterface.logToServer('error', 'Error occured in SelectcameraComponent.ngOnInit', JSON.stringify(error));
    }
  }

  /**
   * Method to close select camera component with selected camera id
   */
  async onConfirm() {
    try {
      this.commInterface.chosenCameraDevice = this.cameraGrp.value.options;
      const cameraPresence = await this.checkCameraPresence();
      if (cameraPresence) {
        this.commInterface.logToServer('info', 'SelectcameraComponent.onConfirm', 'User chosen camera');
        this.dialogRef.close(this.selectedValue);
      }
      else {
        this.commInterface.logToServer('info', 'SelectcameraComponent.onConfirm', 'Camera device cant be accessed');
        this.snackBar.open(
          this.appLabels?.globalLabels?.mediaAccessRequiredMessage?.replace('**device**', 'camera') ??
            `Camera access is required`,
          'Dismiss',
          {
            duration: 3000,
            horizontalPosition: this.snackbarHorizontalPosition,
            verticalPosition: this.snackbarVerticalPosition
          }
        );
        this.dialogRef.close(false);
      }
    } catch (error) {
      console.error('%c Error Occured in onConfirm', 'color: red', error);
      this.commInterface.sendUIErrors('SelectcameraComponent', 'onConfirm', JSON.stringify(error));
      this.commInterface.logToServer(
        'error',
        'Error occured in SelectcameraComponent.onConfirm',
        JSON.stringify(error)
      );
    }
  }

  /**
   * Method to check whether the camera is available with a particular device id
   * @returns Boolean promise based on camera availability
   */
  async checkCameraPresence(): Promise<any> {
    try {
      return new Promise(async (resolve) => {
        try {
          let mediaConstraints: (MediaStreamConstraints & MediaTrackConstraints) | any = {};
          mediaConstraints.video = {
            deviceId: this.commInterface.chosenCameraDevice?.deviceId
          };
          const cameraStream = await this.appdataService.getUserMediaFn(mediaConstraints);
          cameraStream.getTracks().forEach((track) => track.stop());
          resolve(true);
        } catch (error) {
          resolve('unexpected_end');
        }
      });
    } catch (error) {
      console.error('%c Error Occured in checkCameraPresence', 'color: red', error);
      this.commInterface.sendUIErrors('SelectcameraComponent', 'checkCameraPresence', JSON.stringify(error));
      this.commInterface.logToServer(
        'error',
        'Error occured in SelectcameraComponent.checkCameraPresence',
        JSON.stringify(error)
      );
    }
  }

  /**
   * Method to close select camera component without selecting camera id
   */
  onDismiss(): void {
    this.commInterface.logToServer('info', 'SelectcameraComponent.onDismiss', 'User dismissed camera selection');
    this.dialogRef.close(false);
  }

  /**
   * Method to handle camera selection event change from radio button group
   */
  onChanges(): void {
    try {
      if (this.cameraGrp) {
        this.cameraGrp.valueChanges.subscribe((val) => {
          if (this.cameraGrp.valid) {
            for (const i in val) {
              this.selectedValue = val[i].deviceId;
            }
          }
        });
      }
    } catch (error) {
      console.error('%c Error Occured in onChanges', 'color: red', error);
      this.commInterface.sendUIErrors('SelectcameraComponent', 'onChanges', JSON.stringify(error));
      this.commInterface.logToServer(
        'error',
        'Error occured in SelectcameraComponent.onChanges',
        JSON.stringify(error)
      );
    }
  }
}
/**
 * Class to represent confirm dialog model.
 *
 * It has been kept here to keep it as part of shared component.
 */
export class SelectCameraModel {
  constructor(public title: string, public message: any) {}
}
